import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  Command,
  CommandStore,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { Store } from '@softline/core';
import { Router, RouterModule } from '@angular/router';
import {
  HardwareManagementBetriebssystemStore,
  HardwareManagementGarantietypStore,
  HardwareManagementHerstellerStore,
  HardwareManagementKtostammStandortStore,
  HardwareManagementProdukteStore,
  HardwareManagementSubnetzmaskeStore,
  HardwareManagementSupporttypStore,
  HardwareManagementTypStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_BETRIEBSSYSTEM,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_GARANTIETYP,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_HERSTELLER,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_KTOSTAMM_STANDORT,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_PRODKUTE,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUBNETZMASKE,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUPPORTTYP,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_TYP,
} from '../hardware-management.shared';
import { UserInfoService } from '@softline/auth';
import { SoftAppsUserInfo } from '@softapps/core';
import { Refreshable, RefreshService } from '../../../../../../framework/application/src';
import { filter, firstValueFrom } from 'rxjs';
import {
  SOFTLINE_FEATURE_UTILITIES_ACCOUNT,
  UtilitiesAccountStore,
} from '@softapps/core/context';

@Component({
  selector: 'lib-hardware-management',
  imports: [RouterModule],
  template: `
    <router-outlet></router-outlet>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class HardwareManagementComponent implements OnInit, OnDestroy, Refreshable {
  private readonly userInfo = inject<UserInfoService<SoftAppsUserInfo>>(UserInfoService);

  constructor(
    private store: Store,
    protected router: Router,
    private refreshService: RefreshService
  ) {}

  async ngOnInit(): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#HARDWARE_MANAGEMENT.TITLE'
    );
    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: HardwareManagementComponent,
      commands: this.createCommands(),
    });

    this.refreshService.add(this);

    await this.store.dispatch(
      SOFTLINE_FEATURE_UTILITIES_ACCOUNT,
      UtilitiesAccountStore.actions.loadManyOnce
    );
    const accountid = this.userInfo.value()?.accountid;
    const ktostamm = await firstValueFrom(
      this.store
        .observe(
          SOFTLINE_FEATURE_UTILITIES_ACCOUNT,
          UtilitiesAccountStore.getters.entity,
          accountid
        )
        .pipe(filter((o) => !!o))
    );
    await Promise.all([
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_PRODKUTE,
        HardwareManagementProdukteStore.actions.loadManyOnce
      ),
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_TYP,
        HardwareManagementTypStore.actions.loadManyOnce
      ),
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_BETRIEBSSYSTEM,
        HardwareManagementBetriebssystemStore.actions.loadManyOnce
      ),
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_GARANTIETYP,
        HardwareManagementGarantietypStore.actions.loadManyOnce
      ),
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_HERSTELLER,
        HardwareManagementHerstellerStore.actions.loadManyOnce
      ),
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUBNETZMASKE,
        HardwareManagementSubnetzmaskeStore.actions.loadManyOnce
      ),
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUPPORTTYP,
        HardwareManagementSupporttypStore.actions.loadManyOnce
      ),
      this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_KTOSTAMM_STANDORT,
        HardwareManagementKtostammStandortStore.actions.query,
        {
          params: {
            filter: '',
            multiValued: false,
            parameters: { kundennummer: ktostamm.number, lieferadresse: 1 },
          },
        }
      ),
    ]);
  }

  ngOnDestroy(): void {
    this.refreshService.remove(this);
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      HardwareManagementComponent
    );
  }

  async refresh(): Promise<void> {
    const accountid = this.userInfo.value()?.accountid;
    const ktostamm = await firstValueFrom(
      this.store
        .observe(
          SOFTLINE_FEATURE_UTILITIES_ACCOUNT,
          UtilitiesAccountStore.getters.entity,
          accountid
        )
        .pipe(filter((o) => !!o))
    );

    await this.store.dispatch(
      SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_KTOSTAMM_STANDORT,
      HardwareManagementKtostammStandortStore.actions.query,
      {
        params: {
          filter: '',
          multiValued: false,
          parameters: { kundennummer: ktostamm.number, lieferadresse: 1 },
        },
      }
    );
  }

  protected createCommands(): Command[] {
    return [
      {
        icon: 'fa-regular fa-list',
        name: '#HARDWARE_MANAGEMENT.MENU.OVERVIEW',
        class: 'menu main-menu main-menu-top',
        routerLink: ['/hardware-management'],
      },
      {
        icon: 'fa-regular fa-magnifying-glass',
        name: '#HARDWARE_MANAGEMENT.MENU.SEARCH',
        class: 'menu main-menu main-menu-top',
        routerLink: ['/hardware-management/search'],
      },
      {
        icon: 'fa-regular fa-square-plus',
        name: '#HARDWARE_MANAGEMENT.MENU.CREATE',
        class: 'menu main-menu main-menu-top',
        routerLink: ['/hardware-management/create'],
      },
    ];
  }
}
